import { useMemo } from 'react';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { AsinMetadataEntry } from '@graphql/generated/graphql';
import { AgileTableRow, ZoneCapacity, RawItemData } from '@components/agile/types/AgileTableRow';

interface UseAgileTableDataProps {
  otherItems: RawItemData[];
  zoneCapacities: ZoneCapacity[];
  deletedItems: Set<string>;
  asinMetadataList: AsinMetadataEntry[] | null | undefined;
  macroplanogramList: AgileMacroplanogram[] | null | undefined;
  isReset: boolean;
  pendingAsins: string[];
}

export const useAgileManagementTableData = ({
  otherItems,
  zoneCapacities,
  deletedItems,
  asinMetadataList,
  macroplanogramList,
  isReset,
  pendingAsins,
}: UseAgileTableDataProps) => {
  const filteredData = useMemo(() => {
    // First, calculate total allocated facings per zone
    const zoneAllocations = new Map<string, number>();

    otherItems
      .filter((item): item is RawItemData => {
        const itemKey = `${item.asin}-${item.zoneName && item.zoneName !== 'N/A' ? item.zoneName : 'Unassigned'}`;
        return !deletedItems.has(itemKey);
      })
      .forEach((item) => {
        const zoneName = item.zoneName && item.zoneName !== 'N/A' ? item.zoneName : 'Unassigned';
        const currentAllocation = zoneAllocations.get(zoneName) || 0;
        zoneAllocations.set(zoneName, currentAllocation + item.facingUnits);
      });

    const existingData = otherItems
      .filter((item): item is RawItemData => {
        const itemKey = `${item.asin}-${item.zoneName && item.zoneName !== 'N/A' ? item.zoneName : 'Unassigned'}`;
        return !deletedItems.has(itemKey);
      })
      .map((item): AgileTableRow => {
        // Use 'Unassigned' for items without a zone or with 'N/A'
        const zoneName = item.zoneName && item.zoneName !== 'N/A' ? item.zoneName : 'Unassigned';
        const zoneCapacity = zoneCapacities.find((zc) => zc.zoneName.toLowerCase() === zoneName.toLowerCase());
        const macroplanogram = macroplanogramList?.find((mp) => mp.macroplanogramName === zoneName);
        const totalAllocatedInZone = zoneAllocations.get(zoneName) || 0;
        const remainingZoneFacings = zoneCapacity ? zoneCapacity.totalFacings - totalAllocatedInZone : 0;

        // Set remainingFacings to 0 for REMOVE actions when reset is on
        const shouldZeroFacings = isReset && item.action?.action === 'REMOVE';
        const remainingFacings = shouldZeroFacings ? 0 : item.facingUnits;

        // Calculate difference properly based on action
        let difference = 0;
        if (shouldZeroFacings) {
          difference = -item.facingUnits; // REMOVE: negative of current facings
        } else if (item.action?.action === 'ADD') {
          difference = remainingFacings; // ADD: positive difference from 0
        } else {
          difference = remainingFacings - item.facingUnits; // MODIFY: difference between new and old
        }

        return {
          asin: item.asin,
          title: item.title,
          image: item.image || '',
          availableFacings: item.facingUnits,
          remainingFacings: remainingFacings,
          difference: difference,
          remainingCapacity: item.fohCapacity?.capacity || 0,
          remainingZoneFacings: remainingZoneFacings,
          fohCapacity: {
            capacity: item.fohCapacity?.capacity || null,
            jumbleFactor: null,
            errorMsg: null,
          },
          uphl1: item.uphl1 || '',
          uphl2: item.uphl2 || '',
          uphl3: item.uphl3 || '',
          buyable: item.buyable || false,
          procurable: item.procurable || false,
          temperatureRating: item.temperatureRating || '',
          shrinkCost: item.shrinkCost || 0,
          shrinkPerOps: item.shrinkPerOps || 0,
          shrinkPerOps13week: item.shrinkPerOps13week || 0,
          shrinkPerOps26week: item.shrinkPerOps26week || 0,
          shrinkPerOps52week: item.shrinkPerOps52week || 0,
          movingAvgSales13week: item.movingAvgSales13week || 0,
          movingAvgSales26week: item.movingAvgSales26week || 0,
          movingAvgSales52week: item.movingAvgSales52week || 0,
          zoneName: zoneName,
          zoneFacings: zoneCapacity?.totalFacings || 0,
          macroplanogramId: macroplanogram?.entryId || '',
          subcategoryId: item.subcategoryId || '',
          entryId: item.entryId || '', // Add entryId for existing items
          recommendedAction: item.action?.action || '',
          isNewAsin: item.action?.action === 'ADD', // Mark ADD actions as new ASINs
          rowId: item.entryId || `existing-${item.asin}-${zoneName}`, // Use entryId or generate unique ID
        };
      });

    // Add new ASINs with metadata - only when reset flag is off and ASIN is in pendingAsins
    const newAsinData: AgileTableRow[] =
      (!isReset &&
        asinMetadataList
          ?.filter((asinMetadata) => pendingAsins.includes(asinMetadata.asin))
          .map((asinMetadata, index) => ({
            asin: asinMetadata.asin,
            title: asinMetadata.title || 'Unknown',
            image: asinMetadata.image || '',
            availableFacings: 0, // New ASINs start with 0 facings
            remainingFacings: 1, // Default to 1 facing
            difference: 1, // Difference from 0 to 1
            remainingCapacity: asinMetadata.capacityUnitPerFacing?.capacity || 0,
            remainingZoneFacings: 0, // New ASINs start unassigned, so no zone facings
            fohCapacity: {
              capacity: asinMetadata.capacityUnitPerFacing?.capacity || null,
              jumbleFactor: asinMetadata.capacityUnitPerFacing?.jumbleFactor || null,
              errorMsg: asinMetadata.capacityUnitPerFacing?.errorMsg || null,
            },
            uphl1: asinMetadata.uph?.uphl1 || 'Unknown',
            uphl2: asinMetadata.uph?.uphl2 || 'Unknown',
            uphl3: asinMetadata.uph?.uphl3 || 'Unknown',
            buyable: asinMetadata.buyable || false,
            procurable: asinMetadata.procurable || false,
            temperatureRating: String(asinMetadata.temperatureRating || ''),
            shrinkCost: Number(asinMetadata.performanceData?.shrinkCost) || 0,
            shrinkPerOps: Number(asinMetadata.performanceData?.shrinkPerOps) || 0,
            shrinkPerOps13week: Number(asinMetadata.performanceData?.shrinkPerOps13week) || 0,
            shrinkPerOps26week: Number(asinMetadata.performanceData?.shrinkPerOps26week) || 0,
            shrinkPerOps52week: Number(asinMetadata.performanceData?.shrinkPerOps52week) || 0,
            movingAvgSales13week: Number(asinMetadata.performanceData?.movingAvgSales13week) || 0,
            movingAvgSales26week: Number(asinMetadata.performanceData?.movingAvgSales26week) || 0,
            movingAvgSales52week: Number(asinMetadata.performanceData?.movingAvgSales52week) || 0,
            zoneName: 'Unassigned', // New ASINs are unassigned initially
            zoneFacings: 0,
            macroplanogramId: '', // No zone assigned yet
            subcategoryId: '', // New ASINs need subcategory selection
            entryId: '', // New ASINs don't have entryId yet
            isNewAsin: true, // Flag to identify new ASINs
            recommendedAction: 'ADD', // New ASINs have ADD action
            rowId: `new-${asinMetadata.asin}-${Date.now()}-${index}`, // Unique row ID for ag-Grid
          }))) ||
      [];

    const allData = [...newAsinData, ...existingData];

    return allData;
  }, [otherItems, zoneCapacities, deletedItems, asinMetadataList, macroplanogramList, isReset, pendingAsins]);

  return { filteredData };
};
